import { useCart } from 'components/Cart';
import { hasCarisDetect } from 'components/CheckoutML/CheckoutML.utils';
import { useMemo, useRef, useEffect, useCallback } from 'react';
import { useMixpanelExperiment } from 'utils/hooks';
import useProductVariants from 'utils/hooks/useProductVariants';
import { LineItem } from 'utils/types';

import upsellRulesData from './upsell-rules.json';
import upsellRulesDataVariant from './upsell-rules.variant.json';

/**
 * Feature flag to enable/disable checkout upsell functionality.
 * Set to `true` to enable the "For you" tab in checkout with upsell recommendations.
 * Set to `false` to disable checkout upsell (cart sidebar upsell remains unaffected).
 */
export const SHOW_CHECKOUT_UPSELL = false;

/**
 * When true, the Eva upsell is hidden for any cart that contains Caris Detect.
 * Temporary per Caris ask — flip to `false` to re-enable.
 */
export const SUPPRESS_EVA_UPSELL_ON_CARIS = true;

// Type definitions for the JSON structure
interface UpsellRulesData {
  metadata: {
    version: string;
    last_updated: string;
    total_recommendations: number;
    description: string;
    priority_order: string;
    one_recommendation_max: boolean;
  };
  recommendations: Recommendation[];
}

interface Recommendation {
  id: string;
  step: number;
  type: string;
  name: string;
  trigger: {
    cart_contains: number[] | string;
    age_min: number | null;
    age_max: number | null;
    sex_logic: string | null;
    excluded_states: string[];
    trigger_logic?: 'OR' | 'AND';
  };
  action: {
    type: 'REPLACE' | 'ADD';
    remove_products?: number[];
    add_products: number[] | string;
    price_delta: number;
  };
  copy: {
    cart_sidebar: string;
    checkout: {
      under_35: string | null;
      '35_44': string | null;
      '45_54': string | null;
      '55_plus': string | null;
    };
  };
}

export interface UpsellCopy {
  /** Initial greeting message */
  initialMessage: string;
  /** Expanded message with product-specific details (markdown removed) */
  expandedMessage: string;
  /** Expanded message with markdown highlighting preserved */
  expandedMessageWithMarkdown: string;
  /** Accepted state message */
  acceptedMessage: string;
  /** Declined state message */
  declinedMessage: string;
  /** Button text for swapping/adding */
  buttonText: string;
  /** Price difference text (e.g., "$100 more*") */
  priceDifferenceText?: string;
  /** Disclaimer text */
  disclaimerText?: string;
}

export interface UpsellRule {
  /** Recommendation ID */
  id: string;
  /** Step number (priority) */
  step: number;
  /** Type of upsell */
  type: string;
  /** Name of the recommendation */
  name: string;
  /** Action configuration */
  action: {
    type: 'REPLACE' | 'ADD';
    removeProducts?: number[];
    addProducts: number[];
    priceDelta: number;
  };
  /** Copy configuration */
  copy: UpsellCopy;
  /** The line item that triggered this upsell */
  triggerLineItem: LineItem;
}

export interface UpsellConfig {
  /** Whether an upsell should be shown */
  shouldShow: boolean;
  /** The matched upsell rule */
  rule?: UpsellRule;
  /** Whether the upsell product(s) are already in the cart */
  isUpsellInCart: boolean;
}

export interface UseUpsellOptions {
  /** Customer age (optional, for personalized copy) */
  customerAge?: number | null;
  /** Customer sex (optional, for personalized copy and sex logic) */
  customerSex?: 'male' | 'female' | null;
  /** Customer state (optional, for excluded state checks) */
  customerState?: string | null;
  /** Force use of cart_sidebar copy instead of personalized checkout copy */
  preferCartSidebar?: boolean;
  /** Freeze the rule - prevents recalculation when cart changes (useful during swap/add operations) */
  freezeRule?: boolean;
}

/**
 * Determines age bracket from customer age
 */
const getAgeBracket = (
  age: number,
): keyof Recommendation['copy']['checkout'] => {
  if (age < 35) return 'under_35';
  if (age <= 44) return '35_44';
  if (age <= 54) return '45_54';
  return '55_plus';
};

/**
 * Checks if customer age matches trigger requirements
 */
const matchesAgeRequirement = (
  age: number | null | undefined,
  ageMin: number | null,
  ageMax: number | null,
): boolean => {
  if (age === null || age === undefined) {
    // If no age provided, only match if no age restrictions
    return ageMin === null && ageMax === null;
  }
  if (ageMin !== null && age < ageMin) return false;
  if (ageMax !== null && age > ageMax) return false;
  return true;
};

/**
 * Checks if customer sex matches trigger requirements
 */
const matchesSexRequirement = (
  sex: 'male' | 'female' | null | undefined,
  sexLogic: string | null,
): boolean => {
  if (sexLogic === null) return true;
  if (sex === null || sex === undefined) return false;
  if (sexLogic === 'IF_MALE') return sex === 'male';
  if (sexLogic === 'IF_FEMALE') return sex === 'female';
  // SELECT_360_PANEL can be handled separately when processing add_products
  return true;
};

/**
 * Checks if customer state is excluded
 */
const isStateExcluded = (
  state: string | null | undefined,
  excludedStates: string[],
): boolean => {
  if (!state || excludedStates.length === 0) return false;
  return excludedStates.includes(state.toUpperCase());
};

/**
 * Parses product IDs from add_products field
 * Handles both number arrays and special strings like "352_if_male_OR_353_if_female"
 */
const parseAddProducts = (
  addProducts: number[] | string,
  sex?: 'male' | 'female' | null,
): number[] => {
  if (typeof addProducts === 'string') {
    // Handle special cases like "352_if_male_OR_353_if_female"
    if (
      addProducts.includes('_if_male_OR_') ||
      addProducts.includes('SELECT_360_PANEL')
    ) {
      if (sex === 'male') return [352];
      if (sex === 'female') return [353];
      return [];
    }
    // Try to parse as number
    const parsed = parseInt(addProducts, 10);
    return isNaN(parsed) ? [] : [parsed];
  }

  // If it's an array, return it as-is (but ensure it's a number array)
  if (Array.isArray(addProducts)) return addProducts;

  return [];
};

/**
 * Extracts highlighted text from markdown-style **text** format
 */
/*
const extractHighlights = (text: string): string[] => {
  const highlightRegex = /\*\*(.*?)\*\*!/g;
  const matches: string[] = [];
  let match;
  while ((match = highlightRegex.exec(text)) !== null) {
    matches.push(match[1]);
  }
  return matches;
};
*/

/**
 * Removes markdown highlighting from text
 */
const removeMarkdown = (text: string): string =>
  text.replace(/\*\*(.*?)\*\*/g, '$1');

/**
 * Custom hook to determine upsell configuration based on products in the current order
 * Uses rules from upsell-rules.json to match products and return appropriate copy
 *
 * @param options - Optional customer data (age, sex, state) for personalized copy and filtering
 * @returns {UpsellConfig} Configuration object containing upsell rules, copy, and display logic
 *
 * @example
 * ```tsx
 * const { shouldShow, rule, isUpsellInCart } = useUpsell({
 *   customerAge: 35,
 *   customerSex: 'male',
 *   customerState: 'CA'
 * });
 *
 * if (shouldShow && rule) {
 *   return (
 *     <UpsellComponent
 *       message={rule.copy.initialMessage}
 *       expandedMessage={rule.copy.expandedMessage}
 *     />
 *   );
 * }
 * ```
 */
export const useUpsell = (options: UseUpsellOptions = {}): UpsellConfig => {
  const { order } = useCart();
  const productVariants = useProductVariants();
  const experimentVariant = useMixpanelExperiment('sidebar-cart-upsell-v-1');
  const {
    customerAge,
    customerSex,
    customerState,
    preferCartSidebar = false,
    freezeRule = false,
  } = options;
  const rulesData = (
    experimentVariant === 'variant' ? upsellRulesDataVariant : upsellRulesData
  ) as UpsellRulesData;

  // Validate and clear stored upsells if order ID changed
  // Only clear if stored orderId exists and doesn't match current order.id
  useEffect(() => {
    if (typeof localStorage === 'undefined' || !order?.id) return;

    const storage = getUpsellsStorage();
    const currentOrderId = order.id;

    // Check checkout upsell - clear if stored orderId exists and doesn't match
    if (
      storage.checkout?.orderId !== null &&
      storage.checkout?.orderId !== undefined &&
      storage.checkout.orderId !== currentOrderId
    ) {
      removeAcceptedUpsell('checkout');
    }

    // Check cart upsell - clear if stored orderId exists and doesn't match
    if (
      storage.cart?.orderId !== null &&
      storage.cart?.orderId !== undefined &&
      storage.cart.orderId !== currentOrderId
    ) {
      removeAcceptedUpsell('cart-sidebar');
    }
  }, [order?.id]);

  // Store frozen rule to prevent recalculation during swap/add operations
  const frozenRuleRef = useRef<UpsellConfig | null>(null);

  /**
   * Calculate the dynamic price_delta based on actual product prices
   * @param addProductIds - Product IDs being added
   * @param removeProductIds - Product IDs being removed (for REPLACE actions)
   * @returns The calculated price delta (add price - remove price)
   */
  const calculatePriceDelta = useCallback(
    (
      addProductIds: number[],
      removeProductIds: number[] | undefined,
    ): number => {
      let totalAddPrice = 0;
      let totalRemovePrice = 0;

      // Calculate total price of products being added
      for (const productId of addProductIds) {
        const productIdNum = Number(productId);
        // Get product variant info
        const productVariant =
          productVariants[productIdNum] ||
          (productVariants as Record<string, (typeof productVariants)[number]>)[
            String(productIdNum)
          ];

        if (productVariant?.price) {
          const price = parseFloat(productVariant.price);
          totalAddPrice += price;
        }
      }

      // Calculate total price of products being removed (for REPLACE actions)
      if (
        removeProductIds &&
        removeProductIds.length > 0 &&
        order?.line_items
      ) {
        for (const productId of removeProductIds) {
          const productIdNum = Number(productId);
          // Find the line item for this product
          const lineItem = order.line_items.find(
            (item) => Number(item.product_id) === productIdNum,
          );
          if (lineItem?.base_price) {
            const price = parseFloat(lineItem.base_price);
            totalRemovePrice += price;
          }
        }
      }

      // Calculate the actual price delta
      const priceDelta = totalAddPrice - totalRemovePrice;
      return Math.round(priceDelta * 100) / 100; // Round to 2 decimal places
    },
    [productVariants, order?.line_items],
  );

  // Create a serialized version of cart items to track changes
  // This ensures the memo recalculates when items are added/removed
  // Using JSON.stringify to detect changes even if array reference doesn't change
  const cartItemsKey = useMemo(() => {
    if (!order?.line_items || order.line_items.length === 0) {
      return '';
    }
    // Serialize product IDs and quantities to track changes
    // Sort by product_id for consistent comparison
    const items = order.line_items
      .map((item) => {
        const productId =
          item.product_id !== undefined ? Number(item.product_id) : null;
        const quantity = item.quantity || 0;
        return productId !== null ? { productId, quantity } : null;
      })
      .filter(
        (item): item is { productId: number; quantity: number } =>
          item !== null,
      )
      .sort((a, b) => a.productId - b.productId);

    // Use JSON.stringify to ensure we detect changes even if array reference is the same
    return JSON.stringify(items);
  }, [order?.line_items]);

  // Calculate the current rule
  const currentConfig = useMemo(() => {
    // If no order or no line items, don't show upsell
    if (!order?.line_items || order.line_items.length === 0) {
      return {
        shouldShow: false,
        isUpsellInCart: false,
      };
    }

    // Hide Eva upsell for any cart containing Caris Detect
    if (SUPPRESS_EVA_UPSELL_ON_CARIS && hasCarisDetect(order)) {
      return {
        shouldShow: false,
        isUpsellInCart: false,
      };
    }

    const lineItems = order.line_items;
    // Ensure product_id is treated as a number for consistent comparison
    const cartProductIds = lineItems
      .map((item) =>
        item.product_id !== undefined ? Number(item.product_id) : null,
      )
      .filter((id): id is number => id !== null);

    // Sort by step number (ascending: 1, 2, 3, 4, 5...) - lower step = higher priority
    // Handle decimal step numbers (e.g., 1.1, 1.2, 4.2) by sorting numerically
    const sortedRecommendations = [...rulesData.recommendations].sort(
      (a, b) => {
        // Force numeric conversion to handle both integers and decimals (1, 1.1, 1.2, 4, 4.2, etc.)
        const stepA = Number(a.step);
        const stepB = Number(b.step);

        // Handles decimal steps correctly (1.1 < 1.2 < 4 < 4.2)
        return stepA - stepB;
      },
    );

    /**
     * Helper function to determine which expanded message to use
     */
    const getExpandedMessage = (recommendation: Recommendation): string => {
      if (
        preferCartSidebar ||
        customerAge === null ||
        customerAge === undefined ||
        customerSex === null ||
        customerSex === undefined
      ) {
        return recommendation.copy.cart_sidebar;
      }
      const ageBracket = getAgeBracket(customerAge);
      return (
        recommendation.copy.checkout[ageBracket] ||
        recommendation.copy.cart_sidebar
      );
    };

    /**
     * Helper function to get expanded message for fallback recommendations
     */
    const getFallbackExpandedMessage = (fallback: Recommendation): string => {
      if (preferCartSidebar) {
        // Use cart_sidebar copy if available, otherwise fallback to checkout copy
        return (
          fallback.copy.cart_sidebar ||
          fallback.copy.checkout.under_35 ||
          fallback.copy.checkout['35_44'] ||
          fallback.copy.checkout['45_54'] ||
          fallback.copy.checkout['55_plus'] ||
          ''
        );
      } else if (
        customerAge === null ||
        customerAge === undefined ||
        customerSex === null ||
        customerSex === undefined
      ) {
        // Fallback to first available checkout copy if age/sex not available
        return (
          fallback.copy.checkout.under_35 ||
          fallback.copy.checkout['35_44'] ||
          fallback.copy.checkout['45_54'] ||
          fallback.copy.checkout['55_plus'] ||
          ''
        );
      } else {
        // Use age-personalized checkout copy
        const ageBracket = getAgeBracket(customerAge);
        return (
          fallback.copy.checkout[ageBracket] ||
          fallback.copy.checkout.under_35 ||
          fallback.copy.checkout['35_44'] ||
          fallback.copy.checkout['45_54'] ||
          fallback.copy.checkout['55_plus'] ||
          ''
        );
      }
    };

    /**
     * Helper function to build an UpsellRule from a recommendation
     */
    const buildUpsellRule = (
      recommendation: Recommendation,
      triggerLineItem: LineItem,
      customExpandedMessage?: string,
    ): UpsellRule => {
      const { action } = recommendation;

      // Parse add products
      const sexForParsing =
        preferCartSidebar && !customerSex ? null : customerSex;
      const addProductIds = parseAddProducts(
        action.add_products,
        sexForParsing,
      );

      // Calculate dynamic price_delta
      const priceDelta = calculatePriceDelta(
        addProductIds,
        action.remove_products,
      );

      // Get expanded message (use custom if provided, otherwise use helper)
      const expandedMessage =
        customExpandedMessage || getExpandedMessage(recommendation);

      // Build the upsell copy object
      const copy: UpsellCopy = {
        initialMessage: preferCartSidebar
          ? "Here's something to consider"
          : 'Something to consider',
        expandedMessage: removeMarkdown(expandedMessage),
        expandedMessageWithMarkdown: expandedMessage,
        acceptedMessage: "Great choice! I've updated your cart.",
        declinedMessage: "No problem! We'll skip this one.",
        buttonText:
          action.type === 'REPLACE'
            ? `Swap for $${priceDelta} more*`
            : `Add for $${priceDelta} more*`,
        priceDifferenceText: `$${priceDelta} more*`,
        disclaimerText: '* Price does not include taxes and fees',
      };

      // Build and return the rule object
      return {
        id: recommendation.id,
        step: recommendation.step,
        type: recommendation.type,
        name: recommendation.name,
        action: {
          type: action.type,
          removeProducts: action.remove_products,
          addProducts: addProductIds,
          priceDelta,
        },
        copy,
        triggerLineItem,
      };
    };

    /**
     * Helper function to find combination AND rule matches
     */
    const findCombinationMatches = (): Array<{
      recommendation: Recommendation;
      triggerLineItem: LineItem;
      step: number;
    }> => {
      const combinationMatches: Array<{
        recommendation: Recommendation;
        triggerLineItem: LineItem;
        step: number;
      }> = [];

      for (const recommendation of sortedRecommendations) {
        const { trigger, action } = recommendation;
        const currentStep = Number(recommendation.step);

        // Skip if not AND logic
        if (trigger.trigger_logic !== 'AND') {
          continue;
        }

        // Skip if cart_contains is not an array or has less than 2 product IDs
        if (
          typeof trigger.cart_contains === 'string' ||
          !Array.isArray(trigger.cart_contains) ||
          trigger.cart_contains.length < 2
        ) {
          continue;
        }

        // Check if ALL products in cart_contains are in the cart
        const allProductsInCart = trigger.cart_contains.every(
          (ruleProductId) => {
            const ruleProductIdNum = Number(ruleProductId);
            return cartProductIds.includes(ruleProductIdNum);
          },
        );

        if (!allProductsInCart) {
          continue;
        }

        // When preferCartSidebar is true, skip strict customer data checks
        // but still check excluded states
        if (preferCartSidebar) {
          // Skip rules with IF_MALE or IF_FEMALE sex_logic in CartUpsell
          if (
            trigger.sex_logic === 'IF_MALE' ||
            trigger.sex_logic === 'IF_FEMALE'
          ) {
            continue;
          }
        } else {
          // Check age requirement
          if (
            !matchesAgeRequirement(
              customerAge,
              trigger.age_min,
              trigger.age_max,
            )
          ) {
            continue;
          }

          // Check sex requirement
          if (!matchesSexRequirement(customerSex, trigger.sex_logic)) {
            continue;
          }
        }

        // Always check excluded states
        if (isStateExcluded(customerState, trigger.excluded_states)) {
          continue;
        }

        // Parse add products
        const sexForParsing =
          preferCartSidebar && !customerSex ? null : customerSex;
        const addProductIds = parseAddProducts(
          action.add_products,
          sexForParsing,
        );

        // When preferCartSidebar is true, be more lenient
        if (preferCartSidebar) {
          // If we can determine which products to add, check if they're already in cart
          if (addProductIds.length > 0) {
            const isUpsellInCart = addProductIds.some((productId) =>
              cartProductIds.includes(productId),
            );
            if (isUpsellInCart) {
              continue;
            }
          }
        } else {
          // When not preferCartSidebar, be strict
          if (addProductIds.length === 0 && action.add_products !== undefined) {
            continue;
          }

          // Check if any of the upsell products are already in cart
          const isUpsellInCart = addProductIds.some((productId) =>
            cartProductIds.includes(productId),
          );

          if (isUpsellInCart) {
            continue;
          }
        }

        // Find the trigger line item (first matching product)
        // We know cart_contains is an array at this point (checked above)
        const cartContainsArray = trigger.cart_contains as number[];
        const triggerLineItem = lineItems.find((item) => {
          if (item.product_id === undefined) return false;
          const itemProductIdNum = Number(item.product_id);
          return cartContainsArray.some((ruleProductId: number) => {
            const ruleProductIdNum = Number(ruleProductId);
            return itemProductIdNum === ruleProductIdNum;
          });
        });

        if (!triggerLineItem) {
          continue;
        }

        combinationMatches.push({
          recommendation,
          triggerLineItem,
          step: currentStep,
        });
      }

      return combinationMatches;
    };

    // Find the first matching recommendation (one_recommendation_max: true per metadata)
    const allMatches: Array<{
      recommendation: Recommendation;
      triggerLineItem: LineItem;
      step: number;
    }> = [];

    // Create a map of which rules match which cart items
    const cartItemRuleMap: Record<number, string[]> = {};
    cartProductIds.forEach((productId) => {
      cartItemRuleMap[productId] = [];
    });

    // First pass: Find ALL recommendations that match any product in the cart
    for (const recommendation of sortedRecommendations) {
      const { trigger, action } = recommendation;
      // CRITICAL: Ensure step is extracted as a number from the recommendation
      const currentStep = Number(recommendation.step);

      // Handle cart_contains which can be either an array of product IDs or a special string
      const cartContains = trigger.cart_contains;
      let cartContainsRequired: boolean;

      // Check if cart_contains is a string (special case like "ANY_TEST_PRODUCT")
      if (typeof cartContains === 'string') {
        // For special string values, we need to handle them differently
        // "ANY_TEST_PRODUCT" means match if cart has any product (since all products are test products)
        if (cartContains === 'ANY_TEST_PRODUCT') {
          // If cart has any products, it matches
          cartContainsRequired = cartProductIds.length > 0;
        } else {
          // Unknown string value, skip this rule
          continue;
        }
      } else {
        // cart_contains is an array - process normally
        // Check which cart items match this rule's requirements
        const matchingCartItems: number[] = [];
        if (Array.isArray(cartContains)) {
          cartContains.forEach((ruleProductId) => {
            const ruleProductIdNum = Number(ruleProductId);
            if (cartProductIds.includes(ruleProductIdNum)) {
              matchingCartItems.push(ruleProductIdNum);
              // Track which rules match each cart item
              if (cartItemRuleMap[ruleProductIdNum]) {
                cartItemRuleMap[ruleProductIdNum].push(recommendation.id);
              }
            }
          });
        }

        // Check if cart contains required products based on trigger_logic
        // Default to OR for backward compatibility if trigger_logic is missing
        const triggerLogic = trigger.trigger_logic || 'OR';

        // Special case: empty cart_contains array means "match any cart" (fallback rules)
        if (cartContains.length === 0) {
          cartContainsRequired = true;
        } else if (triggerLogic === 'AND') {
          // ALL products in cart_contains must be in cart - verify each specific product ID
          cartContainsRequired = cartContains.every((ruleProductId) => {
            const ruleProductIdNum = Number(ruleProductId);
            return cartProductIds.includes(ruleProductIdNum);
          });
        } else {
          // OR logic: ANY product in cart_contains must be in cart (default) - verify at least one specific product ID
          cartContainsRequired = cartContains.some((ruleProductId) => {
            const ruleProductIdNum = Number(ruleProductId);
            return cartProductIds.includes(ruleProductIdNum);
          });
        }
      }

      if (!cartContainsRequired) {
        continue;
      }

      // When preferCartSidebar is true, skip strict customer data checks
      // but still check excluded states
      if (preferCartSidebar) {
        // Skip rules with IF_MALE or IF_FEMALE sex_logic in CartUpsell
        if (
          trigger.sex_logic === 'IF_MALE' ||
          trigger.sex_logic === 'IF_FEMALE'
        ) {
          continue;
        }
      } else {
        // Check age requirement
        if (
          !matchesAgeRequirement(customerAge, trigger.age_min, trigger.age_max)
        ) {
          continue;
        }

        // Check sex requirement
        if (!matchesSexRequirement(customerSex, trigger.sex_logic)) {
          continue;
        }
      }

      // Always check excluded states (even when preferCartSidebar is true)
      if (isStateExcluded(customerState, trigger.excluded_states)) {
        continue;
      }

      // Parse add products (use customerSex if available, otherwise default to null)
      // When preferCartSidebar is true and customerSex is not available,
      // we'll parse with null sex (which will return empty array for SELECT_360_PANEL)
      const sexForParsing =
        preferCartSidebar && !customerSex ? null : customerSex;
      const addProductIds = parseAddProducts(
        action.add_products,
        sexForParsing,
      );

      // When preferCartSidebar is true, be more lenient - only skip if we can determine
      // the products AND they're already in cart
      if (preferCartSidebar) {
        // If we can determine which products to add, check if they're already in cart
        if (addProductIds.length > 0) {
          const isUpsellInCart = addProductIds.some((productId) =>
            cartProductIds.includes(productId),
          );
          // If upsell products already in cart, skip this recommendation
          if (isUpsellInCart) {
            continue;
          }
        }
        // If we can't determine products (e.g., SELECT_360_PANEL without sex),
        // still proceed - we'll handle it in the component
      } else {
        // When not preferCartSidebar, be strict - skip if we can't determine products
        if (addProductIds.length === 0 && action.add_products !== undefined) {
          continue;
        }

        // Check if any of the upsell products are already in cart
        const isUpsellInCart = addProductIds.some((productId) =>
          cartProductIds.includes(productId),
        );

        // If upsell products already in cart, skip this recommendation
        if (isUpsellInCart) {
          continue;
        }
      }

      // Find the trigger line item (first matching product)
      // Ensure we're comparing numbers correctly
      const triggerLineItem = lineItems.find((item) => {
        if (item.product_id === undefined) return false;
        const itemProductIdNum = Number(item.product_id);

        // Handle both array and string cases for cart_contains
        if (typeof trigger.cart_contains === 'string') {
          // For string cases like "ANY_TEST_PRODUCT", any product matches
          return true;
        } else if (Array.isArray(trigger.cart_contains)) {
          return trigger.cart_contains.some((ruleProductId: number) => {
            const ruleProductIdNum = Number(ruleProductId);
            return itemProductIdNum === ruleProductIdNum;
          });
        }
        return false;
      });

      if (!triggerLineItem) {
        continue;
      }

      allMatches.push({
        recommendation,
        triggerLineItem,
        step: Number(currentStep), // Force numeric conversion
      });
    }

    // After checking ALL recommendations against ALL cart items, find the match with LOWEST step
    // BUT: If cart has 2+ items and no accepted upsell, prioritize combination AND rules
    const hasAtLeastTwoItems = cartProductIds.length >= 2;
    let hasNoAcceptedUpsell = true;
    if (typeof localStorage !== 'undefined') {
      const storage = getUpsellsStorage();
      const storedUpsell = preferCartSidebar ? storage.cart : storage.checkout;
      hasNoAcceptedUpsell = !storedUpsell || storedUpsell.status !== 'accepted';
    }

    // Check for combination AND rules first if conditions are met
    if (hasAtLeastTwoItems && hasNoAcceptedUpsell) {
      const combinationMatches = findCombinationMatches();

      // If we found combination matches, use the one with lowest step (prioritize over individual)
      if (combinationMatches.length > 0) {
        combinationMatches.sort((a, b) => {
          const stepA = Number(a.step);
          const stepB = Number(b.step);
          return stepA - stepB;
        });

        const bestMatch = combinationMatches[0];
        const { recommendation, triggerLineItem } = bestMatch;
        const rule = buildUpsellRule(recommendation, triggerLineItem);

        return {
          shouldShow: true,
          rule,
          isUpsellInCart: false,
        };
      }
    }

    // If no combination matches found (or conditions not met), proceed with individual matches
    if (allMatches.length > 0) {
      // Sort matches by step number (ascending) - lowest step = highest priority
      allMatches.sort((a, b) => {
        const stepA = Number(a.step);
        const stepB = Number(b.step);
        return stepA - stepB;
      });

      const bestMatch = allMatches[0];

      const { recommendation, triggerLineItem } = bestMatch;
      const rule = buildUpsellRule(recommendation, triggerLineItem);

      return {
        shouldShow: true,
        rule,
        isUpsellInCart: false,
      };
    }

    // No matching recommendation found - check for combination AND rules
    if (allMatches.length === 0) {
      // Check if we should look for AND combination rules:
      // 1. Cart has at least 2 items
      // 2. No upsell has been accepted
      // 3. No regular matches were found
      const hasAtLeastTwoItems = cartProductIds.length >= 2;

      // Check if there's an accepted upsell in localStorage
      let hasNoAcceptedUpsell = true;
      if (typeof localStorage !== 'undefined') {
        const storage = getUpsellsStorage();
        const storedUpsell = preferCartSidebar
          ? storage.cart
          : storage.checkout;
        // Only consider it as "accepted" if status is 'accepted'
        hasNoAcceptedUpsell =
          !storedUpsell || storedUpsell.status !== 'accepted';
      }

      if (hasAtLeastTwoItems && hasNoAcceptedUpsell) {
        const combinationMatches = findCombinationMatches();

        // If we found combination matches, use the one with lowest step
        if (combinationMatches.length > 0) {
          combinationMatches.sort((a, b) => {
            const stepA = Number(a.step);
            const stepB = Number(b.step);
            return stepA - stepB;
          });

          const bestMatch = combinationMatches[0];
          const { recommendation, triggerLineItem } = bestMatch;
          const rule = buildUpsellRule(recommendation, triggerLineItem);

          return {
            shouldShow: true,
            rule,
            isUpsellInCart: false,
          };
        }
      }

      // Find both fallback recommendations (360 first, then Multivitamin)
      const fallback360 = sortedRecommendations.find(
        (rec) => rec.type === 'DEFAULT_FALLBACK' && rec.id === '5.1',
      );
      const fallbackMultivitamin = sortedRecommendations.find(
        (rec) => rec.type === 'DEFAULT_FALLBACK' && rec.id === '5.2',
      );

      // Helper function to check if a fallback matches and return the rule
      const tryFallback = (
        fallback: Recommendation | undefined,
      ): UpsellConfig | null => {
        if (!fallback) {
          return null;
        }

        const { trigger, action } = fallback;

        // 360 fallback (5.1) is only for checkout, not cart sidebar
        if (fallback.id === '5.1' && preferCartSidebar) {
          return null;
        }

        // Check age requirement
        // For Multivitamin (5.2) in cart sidebar, ignore age restriction
        if (fallback.id !== '5.2' || !preferCartSidebar) {
          if (
            !matchesAgeRequirement(
              customerAge,
              trigger.age_min,
              trigger.age_max,
            )
          ) {
            return null;
          }
        }

        // Check sex requirement
        // For SELECT_360_PANEL, we need sex to be available to determine the product
        if (trigger.sex_logic === 'SELECT_360_PANEL') {
          if (
            !customerSex ||
            (customerSex !== 'male' && customerSex !== 'female')
          ) {
            return null;
          }
        } else if (!matchesSexRequirement(customerSex, trigger.sex_logic)) {
          return null;
        }

        // Check excluded states
        if (isStateExcluded(customerState, trigger.excluded_states)) {
          return null;
        }

        // Parse add products
        const addProductIds = parseAddProducts(
          action.add_products,
          customerSex,
        );

        // Skip if we can't determine products
        if (addProductIds.length === 0 && action.add_products !== undefined) {
          return null;
        }

        // Check if any of the upsell products are already in cart
        const isUpsellInCart = addProductIds.some((productId) =>
          cartProductIds.includes(productId),
        );

        // If upsell products already in cart, don't show
        if (isUpsellInCart) {
          return null;
        }

        // For default fallback, use the first line item as trigger (since cart_contains is empty)
        const triggerLineItem = lineItems[0];

        if (!triggerLineItem) {
          return null;
        }

        // Get fallback expanded message
        const expandedMessage = getFallbackExpandedMessage(fallback);

        // Build the rule using helper function with custom expanded message
        const rule = buildUpsellRule(
          fallback,
          triggerLineItem,
          expandedMessage,
        );

        return {
          shouldShow: true,
          rule,
          isUpsellInCart: false,
        };
      };

      // In checkout: Try 360 fallback first, then Multivitamin
      // In cart sidebar: Only try Multivitamin (360 is checkout-only)
      if (!preferCartSidebar) {
        // Try 360 fallback first (checkout only)
        const result360 = tryFallback(fallback360);
        if (result360) {
          return result360;
        }
      }

      // Try Multivitamin fallback (works in both checkout and cart sidebar)
      const resultMultivitamin = tryFallback(fallbackMultivitamin);
      if (resultMultivitamin) {
        return resultMultivitamin;
      }
    }

    // No matching recommendation found after checking all recommendations in priority order
    return {
      shouldShow: false,
      isUpsellInCart: false,
    };
  }, [
    order?.line_items,
    customerAge,
    customerSex,
    customerState,
    preferCartSidebar,
    rulesData,
    calculatePriceDelta,
  ]);

  // Freeze the rule when freezeRule is true and we have a valid config
  // Also clear frozen rule when cart changes (cartItemsKey changes)
  useEffect(() => {
    if (freezeRule && currentConfig.shouldShow && currentConfig.rule) {
      // Freeze the current rule when freezeRule becomes true
      frozenRuleRef.current = currentConfig;
    } else if (!freezeRule) {
      // Clear frozen rule when freezeRule is disabled
      frozenRuleRef.current = null;
    }
  }, [freezeRule, currentConfig, cartItemsKey]);

  // Return frozen rule if freezeRule is enabled, otherwise return current config
  const config =
    freezeRule && frozenRuleRef.current ? frozenRuleRef.current : currentConfig;

  return config;
};

const UPSELL_STORAGE_KEY = 'upsell';
const UPSELL_EXPIRATION_TIME_MS = 10 * 60 * 1000; // 10 minutes in milliseconds

export type UpsellSource = 'checkout' | 'cart-sidebar';
export type UpsellStatus = 'accepted' | 'dismissed';

type StoredUpsell = {
  ruleId: string;
  ruleName: string;
  ruleType: string;
  acceptedMessage: string;
  actionType: 'REPLACE' | 'ADD';
  removedLineItems: Array<{
    lineItemId: number;
    variantId: number;
    quantity: number;
  }>;
  addedVariantIds: Array<number>;
  addedProductIds: number[];
  removedProductIds?: number[];
  priceDelta: number;
  expirationTime: string; // ISO string of when this upsell expires (stored time + 10 minutes)
  status: UpsellStatus;
  orderId: number | null;
};

type UpsellsStorage = {
  checkout: StoredUpsell | null;
  cart: StoredUpsell | null;
};

/**
 * Checks if a stored upsell has expired by comparing current time with stored expiration time
 */
const isUpsellExpired = (storedUpsell: StoredUpsell | null): boolean => {
  if (!storedUpsell) return false;

  try {
    // Only check expirationTime - if it doesn't exist, consider it expired
    if (storedUpsell.expirationTime) {
      const expirationTime = new Date(storedUpsell.expirationTime).getTime();
      const currentTime = new Date().getTime();
      return currentTime > expirationTime;
    }

    return true;
  } catch (error) {
    // If expiration time is invalid, consider it expired
    return true;
  }
};

/**
 * Gets the current upsells from localStorage, handling migration from old format
 * Automatically clears expired upsells (older than 10 minutes)
 */
const getUpsellsStorage = (): UpsellsStorage => {
  if (typeof localStorage === 'undefined') {
    return { checkout: null, cart: null };
  }

  try {
    // Check new storage key first
    let storedData = localStorage.getItem(UPSELL_STORAGE_KEY);
    let parsedData = storedData ? JSON.parse(storedData) : null;

    // If not found, try old storage key for migration
    if (!parsedData) {
      const oldStoredData = localStorage.getItem('acceptedUpsell');
      if (oldStoredData) {
        parsedData = JSON.parse(oldStoredData);
      }
    }

    if (!parsedData) return { checkout: null, cart: null };

    // Handle migration from old format (single object with source field)
    if (parsedData?.source) {
      const oldFormatData: StoredUpsell = {
        ruleId: parsedData.ruleId,
        ruleName: parsedData.ruleName,
        ruleType: parsedData.ruleType,
        acceptedMessage: parsedData.acceptedMessage,
        actionType: parsedData.actionType,
        removedLineItems: parsedData.removedLineItems || [],
        addedVariantIds: parsedData.addedVariantIds || [],
        addedProductIds: parsedData.addedProductIds || [],
        removedProductIds: parsedData.removedProductIds,
        priceDelta: parsedData.priceDelta,
        expirationTime: parsedData.expirationTime || '',
        status: 'accepted', // Old format was only for accepted upsells
        orderId: parsedData.orderId || null,
      };

      // Migrate old format to new format
      const migratedStorage: UpsellsStorage = {
        checkout: parsedData.source === 'checkout' ? oldFormatData : null,
        cart: parsedData.source === 'cart-sidebar' ? oldFormatData : null,
      };

      // Save migrated data to new key and remove old key
      localStorage.setItem(UPSELL_STORAGE_KEY, JSON.stringify(migratedStorage));
      localStorage.removeItem('acceptedUpsell');

      return migratedStorage;
    }

    // New format: object with checkout and cart keys
    // Ensure status and orderId fields exist (migration for data without these fields)
    let result: UpsellsStorage = {
      checkout: parsedData?.checkout
        ? {
            ...parsedData.checkout,
            status: parsedData.checkout.status || 'accepted',
            orderId: parsedData.checkout.orderId ?? null,
            // Use expirationTime from stored data - if it doesn't exist, the upsell will be treated as expired
            expirationTime: parsedData.checkout.expirationTime,
          }
        : null,
      cart: parsedData?.cart
        ? {
            ...parsedData.cart,
            status: parsedData.cart.status || 'accepted',
            orderId: parsedData.cart.orderId ?? null,
            // Use expirationTime from stored data - if it doesn't exist, the upsell will be treated as expired
            expirationTime: parsedData.cart.expirationTime,
          }
        : null,
    };

    // Check for expired upsells and clear them
    let hasExpiredUpsells = false;
    if (isUpsellExpired(result.checkout)) {
      result.checkout = null;
      hasExpiredUpsells = true;
    }
    if (isUpsellExpired(result.cart)) {
      result.cart = null;
      hasExpiredUpsells = true;
    }

    // If any upsells were expired, update localStorage
    if (hasExpiredUpsells) {
      if (!result.checkout && !result.cart) {
        // Both are null, remove the entire key
        localStorage.removeItem(UPSELL_STORAGE_KEY);
        localStorage.removeItem('acceptedUpsell');
      } else {
        // Update with cleared expired upsells
        localStorage.setItem(UPSELL_STORAGE_KEY, JSON.stringify(result));
      }
    }

    return result;
  } catch (error) {
    // Silently fail if localStorage is not available or data is malformed
    return { checkout: null, cart: null };
  }
};

/**
 * Stores upsell data in localStorage (accepted or dismissed)
 * @param upsellData - The upsell rule and action details
 * @param source - The source of the upsell (checkout or cart-sidebar)
 * @param status - Whether the upsell was accepted or dismissed
 * @param orderId - The order ID to associate with this upsell (required)
 */
export const storeUpsell = (
  upsellData: {
    rule: UpsellRule;
    acceptedMessage: string;
    removedLineItems: Array<{
      lineItemId: number;
      variantId: number;
      quantity: number;
    }>;
    addedVariantIds: Array<number>;
  },
  source: UpsellSource,
  status: UpsellStatus,
  orderId: number,
): void => {
  if (typeof localStorage === 'undefined' || !orderId) return;

  try {
    const currentStorage = getUpsellsStorage();

    // Calculate expiration time (current time + 10 minutes)
    const expirationTime = new Date(
      Date.now() + UPSELL_EXPIRATION_TIME_MS,
    ).toISOString();

    const dataToStore: StoredUpsell = {
      ruleId: upsellData.rule.id,
      ruleName: upsellData.rule.name,
      ruleType: upsellData.rule.type,
      acceptedMessage: upsellData.acceptedMessage,
      actionType: upsellData.rule.action.type,
      removedLineItems: upsellData.removedLineItems,
      addedVariantIds: upsellData.addedVariantIds,
      addedProductIds: upsellData.rule.action.addProducts,
      removedProductIds: upsellData.rule.action.removeProducts,
      priceDelta: upsellData.rule.action.priceDelta,
      expirationTime,
      status,
      orderId,
    };

    // Store based on source (checkout -> 'checkout', cart-sidebar -> 'cart')
    const storageKey = source === 'checkout' ? 'checkout' : 'cart';
    const newStorage: UpsellsStorage = {
      ...currentStorage,
      [storageKey]: dataToStore,
    };

    localStorage.setItem(UPSELL_STORAGE_KEY, JSON.stringify(newStorage));
  } catch (error) {
    // Silently fail if localStorage is not available or quota exceeded
  }
};

/**
 * @deprecated Use storeUpsell instead
 * Stores accepted upsell data in localStorage
 * @param acceptedData - The accepted upsell rule and action details
 * @param source - The source of the upsell (checkout or cart-sidebar)
 * @param orderId - The order ID to associate with this upsell (required)
 */
export const storeAcceptedUpsell = (
  acceptedData: {
    rule: UpsellRule;
    acceptedMessage: string;
    removedLineItems: Array<{
      lineItemId: number;
      variantId: number;
      quantity: number;
    }>;
    addedVariantIds: Array<number>;
  },
  source: UpsellSource,
  orderId: number,
): void => {
  storeUpsell(acceptedData, source, 'accepted', orderId);
};

/**
 * Removes upsell data from localStorage for a specific source
 * @param source - The source of the upsell to remove (checkout or cart-sidebar). If not provided, removes all upsells.
 */
export const removeAcceptedUpsell = (source?: UpsellSource): void => {
  if (typeof localStorage === 'undefined') return;

  try {
    if (!source) {
      // Remove all upsells
      localStorage.removeItem(UPSELL_STORAGE_KEY);
      // Also remove old key if it exists
      localStorage.removeItem('acceptedUpsell');
      return;
    }

    // Remove only the upsell for the specified source
    const currentStorage = getUpsellsStorage();
    const storageKey = source === 'checkout' ? 'checkout' : 'cart';
    const newStorage: UpsellsStorage = {
      ...currentStorage,
      [storageKey]: null,
    };

    // If both are null, remove the entire key
    if (!newStorage.checkout && !newStorage.cart) {
      localStorage.removeItem(UPSELL_STORAGE_KEY);
      localStorage.removeItem('acceptedUpsell');
    } else {
      localStorage.setItem(UPSELL_STORAGE_KEY, JSON.stringify(newStorage));
    }
  } catch (error) {
    // Silently fail if localStorage is not available
  }
};

/**
 * Checks if there is an upsell in localStorage that is from Checkout
 * @returns true if there is a checkout upsell in localStorage and feature is enabled, false otherwise
 */
export const hasCheckoutUpsellInLocalStorage = (): boolean => {
  // Return false if checkout upsell feature is disabled
  if (!SHOW_CHECKOUT_UPSELL) return false;

  const storage = getUpsellsStorage();
  return storage.checkout !== null;
};

/**
 * Retrieves upsell data from localStorage
 * @param source - The source of the upsell to retrieve (checkout or cart-sidebar). Defaults to 'checkout'.
 * @returns The stored upsell data if it exists and is from the specified source, null otherwise
 */
export const getAcceptedUpsellFromLocalStorage = (
  source: UpsellSource = 'checkout',
): {
  ruleId: string;
  acceptedMessage: string;
  removedLineItems: Array<{
    lineItemId: number;
    variantId: number;
    quantity: number;
  }>;
  addedVariantIds: Array<number>;
  status?: UpsellStatus;
} | null => {
  // Return null for checkout source if feature is disabled
  if (source === 'checkout' && !SHOW_CHECKOUT_UPSELL) return null;

  const storage = getUpsellsStorage();
  const storedUpsell = source === 'checkout' ? storage.checkout : storage.cart;

  if (!storedUpsell) return null;

  return {
    ruleId: storedUpsell.ruleId,
    acceptedMessage: storedUpsell.acceptedMessage,
    removedLineItems: storedUpsell.removedLineItems || [],
    addedVariantIds: storedUpsell.addedVariantIds || [],
    status: storedUpsell.status,
  };
};

/**
 * Checks if there is an upsell in localStorage that is from Cart
 * @returns true if there is a cart upsell in localStorage, false otherwise
 */
export const hasCartUpsellInLocalStorage = (): boolean => {
  const storage = getUpsellsStorage();
  return storage.cart !== null;
};
